const WEIGHT_THRESHOLD_GRAMS = 13000;
const HAISOSM_FORM_SELECTOR = '#haisosm_et';
const CHECK_DEBOUNCE_MS = 300;

const STORAGE_KEY_PATTERN =
  /(haisosm|haiso|haisou|delivery(?:[_-]?(?:date|time))?|selected[_-]?(?:date|time)|希望日|希望時間)/i;

let refreshTimer = null;
let mutationObserver = null;
let isClearingCartAttributes = false;

function scheduleRefresh() {
  if (refreshTimer) {
    window.clearTimeout(refreshTimer);
  }

  refreshTimer = window.setTimeout(() => {
    refreshTimer = null;
    refreshHaisosmByCartWeight();
  }, CHECK_DEBOUNCE_MS);
}

function isFormControl(input) {
  return input instanceof HTMLInputElement || input instanceof HTMLSelectElement || input instanceof HTMLTextAreaElement;
}

function resetInputElement(input) {
  if (input instanceof HTMLSelectElement) {
    input.selectedIndex = 0;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  if (input instanceof HTMLTextAreaElement) {
    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  if (!(input instanceof HTMLInputElement)) return;

  const type = (input.type || '').toLowerCase();
  if (type === 'checkbox' || type === 'radio') {
    input.checked = false;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  if (type === 'button' || type === 'submit' || type === 'reset' || type === 'image' || type === 'file') {
    return;
  }

  input.value = '';
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function clearHaisosmFormValues(container) {
  const controls = container.querySelectorAll('input, select, textarea');
  controls.forEach((element) => {
    if (!isFormControl(element)) return;
    resetInputElement(element);
  });
}

function clearStorageByPattern(storage) {
  try {
    for (let i = storage.length - 1; i >= 0; i -= 1) {
      const key = storage.key(i);
      if (!key) continue;
      if (!STORAGE_KEY_PATTERN.test(key)) continue;
      storage.removeItem(key);
    }
  } catch (error) {
    console.warn('[cart-weight-delivery-control] storage cleanup skipped:', error);
  }
}

function clearCookiesByPattern() {
  const cookies = document.cookie ? document.cookie.split(';') : [];
  cookies.forEach((cookie) => {
    const separatorIndex = cookie.indexOf('=');
    const rawName = separatorIndex >= 0 ? cookie.slice(0, separatorIndex) : cookie;
    const cookieName = rawName.trim();
    if (!cookieName || !STORAGE_KEY_PATTERN.test(cookieName)) return;

    const encodedName = encodeURIComponent(cookieName);
    document.cookie = `${encodedName}=; Max-Age=0; path=/`;
    document.cookie = `${encodedName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  });
}

function clearHaisosmPersistedData() {
  clearStorageByPattern(window.localStorage);
  clearStorageByPattern(window.sessionStorage);
  clearCookiesByPattern();
}

async function clearCartAttributesByPattern() {
  if (isClearingCartAttributes) return;
  isClearingCartAttributes = true;

  try {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart.js for attributes: ${response.status}`);
    }

    const cart = await response.json();
    const attributes = cart?.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};
    const nextAttributes = {};
    let hasTargetAttribute = false;

    Object.keys(attributes).forEach((key) => {
      if (STORAGE_KEY_PATTERN.test(key)) {
        nextAttributes[key] = '';
        hasTargetAttribute = true;
      }
    });

    if (!hasTargetAttribute) return;

    const updateResponse = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: nextAttributes }),
    });

    if (!updateResponse.ok) {
      throw new Error(`Failed to clear cart attributes: ${updateResponse.status}`);
    }
  } catch (error) {
    console.warn('[cart-weight-delivery-control] cart attributes cleanup skipped:', error);
  } finally {
    isClearingCartAttributes = false;
  }
}

async function getCartTotalGrams() {
  const response = await fetch('/cart.js', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch cart.js: ${response.status}`);
  }

  const cart = await response.json();
  const items = Array.isArray(cart?.items) ? cart.items : [];

  return items.reduce((sum, item) => {
    const grams = Number(item?.grams) || 0;
    const quantity = Number(item?.quantity) || 0;
    return sum + grams * quantity;
  }, 0);
}

async function applyHaisosmVisibility(totalGrams) {
  const container = document.querySelector(HAISOSM_FORM_SELECTOR);
  if (!(container instanceof HTMLElement)) return;

  if (container.dataset.originalDisplay == null) {
    container.dataset.originalDisplay = container.style.display || '';
  }

  const shouldHide = totalGrams < WEIGHT_THRESHOLD_GRAMS;
  if (shouldHide) {
    container.style.display = 'none';
    clearHaisosmFormValues(container);
    clearHaisosmPersistedData();
    await clearCartAttributesByPattern();
    return;
  }

  container.style.display = container.dataset.originalDisplay || '';
}

async function refreshHaisosmByCartWeight() {
  try {
    const totalGrams = await getCartTotalGrams();
    await applyHaisosmVisibility(totalGrams);
  } catch (error) {
    console.warn('[cart-weight-delivery-control] failed to refresh:', error);
  }
}

function setupMutationObserver() {
  const target =
    document.querySelector('cart-items-component') ||
    document.querySelector('#MainContent') ||
    document.querySelector('main');

  if (!(target instanceof HTMLElement)) return;

  mutationObserver?.disconnect();
  mutationObserver = new MutationObserver(() => {
    scheduleRefresh();
  });
  mutationObserver.observe(target, {
    childList: true,
    subtree: true,
  });
}

function setup() {
  refreshHaisosmByCartWeight();
  setupMutationObserver();

  document.addEventListener('cart:update', scheduleRefresh);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setup, { once: true });
} else {
  setup();
}
